<script lang="ts" setup>
import { ref, onMounted, nextTick, watch, onUnmounted } from 'vue'
import { Tooltip } from 'ant-design-vue'
import TextView from '@/ui/common/text-view/TextView.vue'
import {ITextViewColor, ITextViewVariant} from "@/ui/common/text-view/types";

interface Props {
  text: string
  lines?: number
  variant?: ITextViewVariant
  color?: ITextViewColor
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  lines: 3,
  variant: 'roboto_13_regular',
  color: 'gray_100'
})

const textRef = ref<InstanceType<typeof TextView>>()
const isTextOverflowed = ref(false)

const checkTextOverflow = (): boolean => {
  const element = textRef.value?.$el || textRef.value;
  if (!element) return false;

  const domElement = element instanceof HTMLElement ? element : element.$el;
  if (!domElement) return false;

  const computedStyle = window.getComputedStyle(domElement);
  const webkitLineClamp = computedStyle.webkitLineClamp;

  if (webkitLineClamp && webkitLineClamp !== 'none') {
    const lineClamp = parseInt(webkitLineClamp);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
    const maxHeight = lineClamp * lineHeight;
    return Math.ceil(domElement.scrollHeight) > Math.ceil(maxHeight) + 1;
  }

  return false;
}

const updateOverflowState = async () => {
  await nextTick();
  setTimeout(() => {
    isTextOverflowed.value = checkTextOverflow();
  }, 100);
}

onMounted(() => {
  updateOverflowState();
})

watch(() => props.text, () => {
  updateOverflowState();
})

let resizeObserver: ResizeObserver | null = null;

watch(textRef, (newValue) => {
  if (resizeObserver) resizeObserver.disconnect();

  if (newValue) {
    const element = newValue.$el || newValue;
    const domElement = element instanceof HTMLElement ? element : element.$el;

    if (domElement) {
      resizeObserver = new ResizeObserver(updateOverflowState);
      resizeObserver.observe(domElement);
    }
  }
}, { immediate: true });

onUnmounted(() => {
  if (resizeObserver) resizeObserver.disconnect();
})
</script>

<template>
  <Tooltip
      :title="isTextOverflowed ? text : null"
      placement="top"
      :mouse-enter-delay="0.3"
      :destroy-tooltip-on-hide="true"
      overlay-class-name="TruncatedTextTooltip"
  >
    <TextView
        ref="textRef"
        :variant="variant"
        :color="color"
        :class="['TruncatedText', $attrs.class]"
        :style="{
        'display': '-webkit-box',
        '-webkit-line-clamp': lines,
        '-webkit-box-orient': 'vertical',
        'overflow': 'hidden',
        'text-overflow': 'ellipsis'
      }"
    >
      {{ text }}
    </TextView>
  </Tooltip>
</template>

<style>
.TruncatedTextTooltip .ant-tooltip-inner {
  font-size: 12px;
  line-height: 1.2;
  padding: 8px;
  max-width: 400px;
}
</style>